import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Komunikat, Pole, Przycisk } from '../components/ui';

/**
 * Ekran wymuszany przy pierwszym logowaniu i po resecie hasła. Do czasu zmiany
 * serwer odrzuca wszystkie pozostałe żądania — to nie jest tylko zasłona w UI.
 */
export function ZmianaHasla({ wymuszona }: { wymuszona: boolean }) {
  const qc = useQueryClient();
  const { wyloguj } = useAuth();
  const [stareHaslo, setStare] = useState('');
  const [noweHaslo, setNowe] = useState('');
  const [powtorz, setPowtorz] = useState('');
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);
  const [gotowe, setGotowe] = useState(false);
  const [wysylanie, setWysylanie] = useState(false);

  async function wyslij(e: FormEvent) {
    e.preventDefault();
    setBlad(null);
    setBledy({});

    if (noweHaslo !== powtorz) {
      setBledy({ powtorz: 'Hasła nie są takie same.' });
      return;
    }

    setWysylanie(true);
    try {
      await api.post('/auth/zmiana-hasla', { stareHaslo, noweHaslo });
      setGotowe(true);
      await qc.invalidateQueries({ queryKey: ['ja'] });
    } catch (err) {
      if (err instanceof BladApi) {
        setBledy(err.bledyPol);
        if (Object.keys(err.bledyPol).length === 0) setBlad(err.message);
      } else {
        setBlad('Nie udało się zmienić hasła.');
      }
    } finally {
      setWysylanie(false);
    }
  }

  const tresc = (
    <form onSubmit={wyslij} className="flex flex-col gap-4">
      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}
      {gotowe && <Komunikat rodzaj="sukces">Hasło zmienione.</Komunikat>}

      <Pole
        etykieta="Dotychczasowe hasło"
        type="password"
        value={stareHaslo}
        onChange={(e) => setStare(e.target.value)}
        autoComplete="current-password"
        blad={bledy.stareHaslo}
        required
      />
      <Pole
        etykieta="Nowe hasło"
        type="password"
        value={noweHaslo}
        onChange={(e) => setNowe(e.target.value)}
        autoComplete="new-password"
        blad={bledy.noweHaslo}
        podpowiedz="Co najmniej 10 znaków. Długość chroni lepiej niż znaki specjalne — dwa zapamiętane słowa wystarczą."
        required
      />
      <Pole
        etykieta="Powtórz nowe hasło"
        type="password"
        value={powtorz}
        onChange={(e) => setPowtorz(e.target.value)}
        autoComplete="new-password"
        blad={bledy.powtorz}
        required
      />

      <div className="mt-2 flex gap-3">
        <Przycisk type="submit" wariant="glowny" disabled={wysylanie}>
          {wysylanie ? 'Zapisywanie...' : 'Zmień hasło'}
        </Przycisk>
        {wymuszona && (
          <Przycisk type="button" wariant="cichy" onClick={() => void wyloguj()}>
            Wyloguj
          </Przycisk>
        )}
      </div>
    </form>
  );

  if (!wymuszona) {
    return (
      <div className="mx-auto max-w-md">
        <h1 className="mb-1 text-xl font-semibold text-kamien-900">Zmiana hasła</h1>
        <p className="mb-6 text-sm text-kamien-500">
          Zmiana hasła kończy Twoje sesje na innych stanowiskach.
        </p>
        <div className="rounded-lg bg-white p-6 ring-1 ring-kamien-200">{tresc}</div>
      </div>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-kamien-100 px-4 py-10">
      <div className="w-full max-w-md">
        <h1 className="mb-1 text-xl font-semibold text-kamien-900">Ustaw własne hasło</h1>
        <p className="mb-6 text-sm text-kamien-600">
          Hasło otrzymane od administratora działa tylko do pierwszego logowania.
          Zanim zaczniesz pracę, ustaw własne — Twoje operacje będą podpisane Twoim kontem.
        </p>
        <div className="rounded-lg bg-white p-6 ring-1 ring-kamien-200">{tresc}</div>
      </div>
    </main>
  );
}
